import { ref, computed, watch, onMounted } from 'vue'

// Global theme state
const globalTheme = ref<'day' | 'night'>('day')
const isSampleProductPage = ref(false)
const clientReady = ref(false)
let hasClientSetup = false

// Detect system preference
const getSystemTheme = (): 'day' | 'night' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day'
  }
  return 'day'
}

// Load theme from localStorage or system preference
const loadGlobalTheme = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const saved = localStorage.getItem('global-theme')
    if (saved === 'day' || saved === 'night') {
      globalTheme.value = saved
    } else {
      // No saved preference, use system preference
      globalTheme.value = getSystemTheme()
    }
  } else {
    // Fallback to system preference
    globalTheme.value = getSystemTheme()
  }
}

// Save theme to localStorage
const saveGlobalTheme = (theme: 'day' | 'night') => {
  globalTheme.value = theme
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.setItem('global-theme', theme)
  }
}

// Toggle between day and night (overrides system preference)
const toggleGlobalTheme = () => {
  const newTheme = globalTheme.value === 'day' ? 'night' : 'day'
  saveGlobalTheme(newTheme)
}

// Reset to system preference
const resetToSystemTheme = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.removeItem('global-theme')
  }
  globalTheme.value = getSystemTheme()
}

// Listen for system theme changes
const setupSystemThemeListener = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      const saved = localStorage.getItem('global-theme')
      if (!saved) {
        globalTheme.value = e.matches ? 'night' : 'day'
      }
    }
    
    mediaQuery.addEventListener('change', handleSystemThemeChange)
    
    // Return cleanup function
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange)
  }
  return () => {}
}
const ensureClientSetup = () => {
  if (!clientReady.value) {
    loadGlobalTheme()
    setupSystemThemeListener()
    clientReady.value = true
  }
}

// Get current effective theme
const getEffectiveTheme = () => {
  // During SSR or before client hydration, fall back to default theme
  if (!clientReady.value) {
    return isSampleProductPage.value ? 'content-studio' : globalTheme.value
  }
  
  // If on sample product page, use Content Studio theme
  if (isSampleProductPage.value) {
    return 'content-studio' // Special flag for Content Studio theme
  }
  
  // Check if user has set a global theme preference
  const hasGlobalThemePreference = typeof window !== 'undefined' && window.localStorage && localStorage.getItem('global-theme')
  
  if (hasGlobalThemePreference) {
    // Use global theme if user has set a preference
    return globalTheme.value
  } else {
    // No user preference set, use system theme
    return getSystemTheme()
  }
}

// Set sample product page state
const setSampleProductPage = (isSample: boolean) => {
  isSampleProductPage.value = isSample
}

// Get the effective background color for the sample product page
const getSampleProductPageBackgroundColor = () => {
  if (!clientReady.value) {
    return globalTheme.value === 'night' ? '#000000' : '#ffffff'
  }
  
  if (typeof window !== 'undefined' && window.localStorage) {
    // Check if there's a saved Content Studio background color
    const savedContentData = localStorage.getItem('content-studio-data')
    if (savedContentData) {
      try {
        const parsed = JSON.parse(savedContentData)
        if (parsed.backgroundColor) {
          return parsed.backgroundColor
        }
      } catch (e) {
        console.warn('Failed to parse saved Content Studio data:', e)
      }
    }
  }
  
  // If no Content Studio data, check if user has set a global theme preference
  const hasGlobalThemePreference = typeof window !== 'undefined' && window.localStorage && localStorage.getItem('global-theme')
  
  if (hasGlobalThemePreference) {
    // Use global theme if user has set a preference
    return globalTheme.value === 'night' ? '#000000' : '#ffffff'
  } else {
    // No user preference set, use system theme
    const systemTheme = getSystemTheme()
    return systemTheme === 'night' ? '#000000' : '#ffffff'
  }
}

// Initialize on client side only

export const useGlobalTheme = () => {
  // Initialize theme on client side when composable is used
  if (typeof window !== 'undefined') {
    onMounted(() => {
      if (!hasClientSetup) {
        hasClientSetup = true
        ensureClientSetup()
      }
    })
  }

  return {
    globalTheme: computed(() => globalTheme.value),
    isSampleProductPage: computed(() => isSampleProductPage.value),
    getEffectiveTheme,
    toggleGlobalTheme,
    resetToSystemTheme,
    setSampleProductPage,
    saveGlobalTheme,
    getSampleProductPageBackgroundColor,
    getSystemTheme
  }
}
